import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { getFilterOptions } from 'modules/automation/utils/getFilterOptions';
import { LineWithHover } from '.';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import {
  VIEWED_UNVIEWED,
  BY_PRACTICE_AREA,
  BY_VENUE,
  BY_CASE_CLOSURE_REASON,
  BY_CASE_MATTER,
  BY_CASE_OUTCOME,
  BY_CASE_STAGE,
  BY_EVENT_GUESTS,
  BY_EVENT_LOCATION,
  BY_EVENT_TYPE,
  BY_LEAD_PRIORITY,
  BY_LEAD_STAGE,
  BY_POTENTIAL_CASE_VALUE,
  BY_TAG,
  BY_TASK_DUE_DATE,
  BY_TASK_PRIORITY,
  BY_POSITION,
} from 'constants/automation';
import {
  CaseClosureReasonFilter,
  CaseMatterFilter,
  CaseOutcomeFilter,
  CaseStageFilter,
  DummyFilter,
  EventGuestFilter,
  EventLocationFilter,
  EventTypeFilter,
  IsViewedFilter,
  LeadPriorityFilter,
  LeadStageFilter,
  PotentialCaseValueFilter,
  PracticeAreaFilter,
  TagFilter,
  TaskDueDateFilter,
  TaskPriorityFilter,
  VenueFilter,
  PositionFilter,
} from './filters';
import { useCaseMatter } from 'hooks/useCaseMatter';
import { useVenueList } from 'hooks/useVenueList';
import useGetEventTypes from 'modules/file_manager/hooks/useGetEventTypes';
import useLeadStages from 'hooks/useLeadStages';
import { useGlobalTagList } from 'hooks/useGlobalTags';
import { usePracticeAreaList } from 'hooks/usePracticeAreaList';
const FilterBlock = () => {
  const {
    filterMethods,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const trigger = watch('trigger');

  const practiceAreaId = useMemo(() => {
    const filters = watch('filters');
    return filters?.filter((f) => f?.label === BY_PRACTICE_AREA)?.map((f) => f?.value?.['case_description.case_practice_area'] || []);
  }, [JSON.stringify(watch('filters'))]);

  const leadStagePracticeAreaIds = useMemo(() => {
    return practiceAreaId?.flat()?.map((id) => `practice_area#${id}`) || [];
  }, [practiceAreaId]);

  const filterOptions = useMemo(() => getFilterOptions(trigger?.module, trigger?.code), [trigger?.module, trigger?.code]);
  const selectedOptions = watch('filters')?.map((f) => f?.label);

  const isCaseMatterSelected = useMemo(() => selectedOptions?.includes(BY_CASE_MATTER), [selectedOptions]);
  const isVenueSelected = useMemo(() => selectedOptions?.includes(BY_VENUE), [selectedOptions]);
  const isEventTypeSelected = useMemo(() => selectedOptions?.includes(BY_EVENT_TYPE), [selectedOptions]);
  const isLeadStageTypeSelected = useMemo(() => selectedOptions?.includes(BY_LEAD_STAGE), [selectedOptions]);
  const isTagTypeSelected = useMemo(() => selectedOptions?.includes(BY_TAG), [selectedOptions]);

  const { practiceAreaList } = usePracticeAreaList(isCaseMatterSelected ? practiceAreaId : []);
  const { loadCaseMatter, caseMatterList } = useCaseMatter(true, isCaseMatterSelected ? practiceAreaId : '');
  const { loadVenue, venueOptions } = useVenueList(true, isVenueSelected && practiceAreaId);
  const { getEventTypes, typeList } = useGetEventTypes(isEventTypeSelected && practiceAreaId);
  const { leadStages, loadLeadStages } = useLeadStages(leadStagePracticeAreaIds);
  const { formattedGlobalTag, loadGlobalList } = useGlobalTagList(isTagTypeSelected && practiceAreaId);
  const { fields, append, remove } = filterMethods || {};

  const practiceAreaListId = useMemo(() => practiceAreaList?.map((item) => `practice_area#${item.practice_area_id}`), [practiceAreaList]);

  return (
    <>
      <LineWithHover showAddFilter={false} showAddAction={false} />
      <div className="p-3 card-bg filter-section">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-start">
            <i className="pi pi-filter text-primary me-2" style={{ fontSize: '0.9rem', marginTop: '2px' }}></i>
            <div className="d-flex">
              <h6 className="sub-title text-gray-500 mb-1">Filters</h6>
              <span className="badge text-primary-dark ms-2 d-flex align-items-center">Applies to all actions</span>
            </div>
          </div>
          <Button
            tooltip="Remove"
            tooltipOptions={{ showDelay: 500 }}
            icon="pi pi-trash danger-text"
            className="p-button-rounded p-button-text text-danger"
            onClick={() => remove()}
          />
        </div>

        {fields.map((item, index) => {
          const value = getValues(`filters.${index}.label`);
          return (
            <div key={item.id} className="d-flex align-items-center mb-3 p-3 input-shadow filterbar-row">
              <div className="me-2 filterbar-field">
                <Controller
                  name={`filters.${index}.label`}
                  control={control}
                  rules={{ required: 'Condition is required' }}
                  render={({ field }) => (
                    <Dropdown
                      options={filterOptions}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.value);
                        setValue(`filters.${index}.value`, '');
                        setValue(`filters.${index}.condition`, '');
                      }}
                      placeholder="Select Field"
                      className="w-100"
                      optionDisabled={(e) => selectedOptions?.includes(e.value)}
                      inputRef={field.ref}
                    />
                  )}
                />
                <RequiredValidation errors={errors} field={`filters.${index}.label`} />
              </div>
              {BY_PRACTICE_AREA === value && (
                <PracticeAreaFilter
                  index={index}
                  loadCaseMatter={loadCaseMatter}
                  loadVenue={loadVenue}
                  getEventTypes={getEventTypes}
                  isCaseMatterSelected={isCaseMatterSelected}
                  isVenueSelected={isVenueSelected}
                  isEventTypeSelected={isEventTypeSelected}
                  loadLeadStages={loadLeadStages}
                  isLeadStageTypeSelected={isLeadStageTypeSelected}
                  loadGlobalList={loadGlobalList}
                  isTagTypeSelected={isTagTypeSelected}
                />
              )}{' '}
              {/*completed*/}
              {BY_VENUE === value && <VenueFilter index={index} practiceAreaId={practiceAreaId} venueListOptions={venueOptions} />}{' '}
              {/*completed*/}
              {VIEWED_UNVIEWED === value && <IsViewedFilter index={index} />}
              {BY_EVENT_GUESTS === value && <EventGuestFilter index={index} />}
              {BY_EVENT_LOCATION === value && <EventLocationFilter index={index} />}
              {BY_EVENT_TYPE === value && <EventTypeFilter index={index} practiceAreaId={practiceAreaId} typeListOptions={typeList} />}
              {BY_TASK_PRIORITY === value && <TaskPriorityFilter index={index} />}
              {BY_TASK_DUE_DATE === value && <TaskDueDateFilter index={index} />} {/*TimeZone not handled*/}
              {BY_CASE_MATTER === value && (
                <CaseMatterFilter index={index} practiceAreaId={practiceAreaId} caseMatterOptions={caseMatterList} />
              )}
              {BY_CASE_CLOSURE_REASON === value && <CaseClosureReasonFilter index={index} />}
              {BY_CASE_OUTCOME === value && <CaseOutcomeFilter index={index} />}
              {BY_LEAD_STAGE === value && (
                <LeadStageFilter
                  index={index}
                  practiceAreaId={practiceAreaId}
                  leadStages={leadStages}
                  practiceAreaListId={practiceAreaListId}
                />
              )}
              {BY_CASE_STAGE === value && <CaseStageFilter index={index} />}
              {BY_POTENTIAL_CASE_VALUE === value && <PotentialCaseValueFilter index={index} />}
              {BY_TAG === value && <TagFilter index={index} practiceAreaId={practiceAreaId} globalList={formattedGlobalTag} />}
              {BY_LEAD_PRIORITY === value && <LeadPriorityFilter index={index} />}
              {BY_POSITION === value && <PositionFilter index={index} />}
              {!value && <DummyFilter />}
              <Button
                tooltip="Remove"
                disabled={fields.length === 1 && index === 0}
                tooltipOptions={{ showDelay: 500 }}
                icon="pi pi-times"
                className="p-button-rounded p-button-text text-danger filterbar-remove"
                onClick={() => {
                  remove(index);
                  if (value === BY_PRACTICE_AREA) {
                    loadCaseMatter([]);
                    loadVenue([]);
                    getEventTypes([]);
                  }
                }}
              />
            </div>
          );
        })}
        <div className="d-flex justify-content-center">
          <Button
            label="Add Filter"
            icon="pi pi-plus"
            className="p-button-text text-primary mt-2"
            onClick={() => append({ label: '', condition: '', value: '' })}
          />
        </div>
      </div>
    </>
  );
};

export default FilterBlock;
